import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/config";
import { useNavigate, useSearchParams } from "react-router-dom";
import { differenceInDays } from "date-fns";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { Check, X, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getStoredAffiliateCode, getAffiliateCouponCode, AFFILIATE_FRIEND_COUPON } from "@/hooks/useAffiliateTracking";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DowngradeModal } from "@/components/DowngradeModal";
import { 
  formatDiscountMessage, 
  saveCouponData, 
  clearCouponData,
  getFormattedProductList,
  hasValidCoupons,
  getFirstValidCoupon,
  validateCouponGlobally,
  type CouponResult 
} from "@/lib/couponUtils";

const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, subscriptionType, subscriptionEndDate, hasActiveSubscription, loadingSubscription, hasCompletedScreening, screeningStatus, refreshSubscription } = useAuth();
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupons, setAppliedCoupons] = useState<Record<string, CouponResult | null>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const [pendingPlanChange, setPendingPlanChange] = useState<{ planId: string; billingType: string } | null>(null);
  const [showVisibilityInfo, setShowVisibilityInfo] = useState(false);
  
  // Active visibility packages state
  const [hasActiveSpotlight, setHasActiveSpotlight] = useState(false);
  const [hasActiveSocialBoost, setHasActiveSocialBoost] = useState(false);
  const [spotlightEndDate, setSpotlightEndDate] = useState<Date | null>(null);
  const [socialBoostEndDate, setSocialBoostEndDate] = useState<Date | null>(null);
  
  // Helper to check if any coupon is valid
  const hasAnyCouponApplied = Object.values(appliedCoupons).some(c => c?.valid);
  
  // Get a sample valid coupon for display
  const getSampleValidCoupon = () => Object.values(appliedCoupons).find(c => c?.valid);

  // Load coupon from URL params or sessionStorage on mount
  useEffect(() => {
    const urlCoupon = searchParams.get('coupon');
    
    if (urlCoupon) {
      // Save to sessionStorage for persistence
      sessionStorage.setItem('pendingCoupon', urlCoupon.toUpperCase());
      setCouponCode(urlCoupon.toUpperCase());
      
      // Auto-validate if user is authenticated - show toast only for URL coupon (first visit)
      if (isAuthenticated) {
        validateCouponCode(urlCoupon.toUpperCase(), true);
      }
    } else {
      // Check for saved coupon in sessionStorage
      const savedCoupon = sessionStorage.getItem('pendingCoupon');
      const savedCouponData = sessionStorage.getItem('appliedCouponData');
      
      if (savedCoupon) {
        setCouponCode(savedCoupon);
        
        // If we have saved coupon data, restore it silently (no toast)
        if (savedCouponData) {
          try {
            const parsedData = JSON.parse(savedCouponData);
            setAppliedCoupons(parsedData);
            // No toast - just restore data
          } catch (e) {
            // If parsing fails, validate silently
            if (isAuthenticated) {
              validateCouponCode(savedCoupon, false);
            }
          }
        } else if (isAuthenticated) {
          // No saved data, validate silently
          validateCouponCode(savedCoupon, false);
        }
      }
    }
  }, [isAuthenticated, searchParams]);

  // Save applied coupon data to sessionStorage whenever it changes
  useEffect(() => {
    if (hasAnyCouponApplied) {
      sessionStorage.setItem('appliedCouponData', JSON.stringify(appliedCoupons));
      sessionStorage.setItem('pendingCoupon', couponCode);
    } else {
      sessionStorage.removeItem('appliedCouponData');
    }
  }, [appliedCoupons, couponCode, hasAnyCouponApplied]);

  // Auto-apply FRIEND coupon if affiliate code exists and user has no active subscription
  useEffect(() => {
    const affiliateCoupon = getAffiliateCouponCode();
    
    // Only auto-apply if:
    // - Affiliate code exists (so FRIEND coupon should be applied)
    // - User is authenticated
    // - User does NOT have an active subscription
    // - No coupon is already applied
    // - Not currently validating
    if (
      affiliateCoupon && 
      isAuthenticated && 
      !hasActiveSubscription && 
      !hasAnyCouponApplied && 
      !couponCode &&
      !isValidating
    ) {
      setCouponCode(affiliateCoupon);
      validateCouponCode(affiliateCoupon, true);
      toast({
        title: t('pricing.coupon.friendAutoApplied'),
      });
    }
  }, [isAuthenticated, hasActiveSubscription, hasAnyCouponApplied, couponCode, isValidating]);

  // Check for active visibility packages
  useEffect(() => {
    const checkActivePackages = async () => {
      if (!isAuthenticated) return;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check spotlight
      const { data: spotlight } = await supabase
        .from("featured_professionals")
        .select("id, end_date")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .eq("feature_type", "spotlight")
        .gte("end_date", new Date().toISOString())
        .maybeSingle();

      setHasActiveSpotlight(!!spotlight);
      setSpotlightEndDate(spotlight?.end_date ? new Date(spotlight.end_date) : null);

      // Check social_boost
      const { data: socialBoost } = await supabase
        .from("subscriptions")
        .select("id, current_period_end")
        .eq("user_id", user.id)
        .eq("product_type", "social_boost")
        .eq("status", "active")
        .gte("current_period_end", new Date().toISOString())
        .maybeSingle();

      setHasActiveSocialBoost(!!socialBoost);
      setSocialBoostEndDate(socialBoost?.current_period_end ? new Date(socialBoost.current_period_end) : null);
    };

    checkActivePackages();
  }, [isAuthenticated]);

  const plans = [
    {
      id: "base",
      nameKey: "pricing.plans.base.name",
      featuresKey: "pricing.plans.base.features",
      price: 70,
      duration: "annual",
      popular: false,
    },
    {
      id: "pro",
      nameKey: "pricing.plans.pro.name",
      featuresKey: "pricing.plans.pro.features",
      price: 95,
      duration: "annual",
      popular: true,
    },
    {
      id: "plus",
      nameKey: "pricing.plans.plus.name",
      featuresKey: "pricing.plans.plus.features",
      price: 185,
      duration: "annual",
      popular: false,
    },
    {
      id: "spotlight",
      nameKey: "pricing.plans.spotlight.name",
      featuresKey: "pricing.plans.spotlight.features",
      price: 149,
      duration: "one_time_28_days",
      popular: false,
    },
    {
      id: "social_boost",
      nameKey: "pricing.plans.socialBoost.name",
      featuresKey: "pricing.plans.socialBoost.features",
      price: 399,
      duration: "one_time_28_days",
      popular: false,
    },
  ];

  // Gerarchia piani per upgrade/downgrade
  const planHierarchy = {
    base: { order: 1, name: 'Base' },
    pro: { order: 2, name: 'Pro' },
    plus: { order: 3, name: 'Plus' },
  };

  // Helper per distinguere tipi di prodotto
  const isSubscriptionPlan = (planId: string) => ['base', 'pro', 'plus'].includes(planId);
  const isVisibilityPackage = (planId: string) => ['spotlight', 'social_boost'].includes(planId);

  // Helper per determinare azione bottone (SOLO per piani subscription)
  const getPlanAction = (planId: string) => {
    if (!isSubscriptionPlan(planId)) return 'select'; // Visibility packages non hanno upgrade/downgrade
    if (!subscriptionType) return 'select';
    
    const currentOrder = planHierarchy[subscriptionType as keyof typeof planHierarchy]?.order || 0;
    const targetOrder = planHierarchy[planId as keyof typeof planHierarchy]?.order || 0;
    
    if (currentOrder === targetOrder) return 'current';
    if (targetOrder > currentOrder) return 'upgrade';
    return 'downgrade';
  };

  // Determina quali piani mostrare
  const getVisiblePlans = () => {
    const recurringPlans = plans.slice(0, 3);
    const visibilityPackages = plans.slice(3);
    
    if (!isAuthenticated || !hasCompletedScreening) {
      return { recurring: recurringPlans, visibility: [] };
    }
    
    return { recurring: recurringPlans, visibility: visibilityPackages };
  };

  const { recurring: visibleRecurringPlans, visibility: visibleVisibilityPackages } = getVisiblePlans();

  const validateCouponCode = async (code: string, showToast = true) => {
    if (!code.trim()) {
      if (showToast) toast.error(t('pricing.coupon.errorEmpty'));
      return;
    }

    setIsValidating(true);

    try {
      // Use centralized batch validation
      const results = await validateCouponGlobally(supabase, code.toUpperCase());
      setAppliedCoupons(results);

      const hasAnyValid = hasValidCoupons(results);

      if (showToast) {
        if (!hasAnyValid) {
          toast.error(t('pricing.coupon.invalid'));
        } else {
          const locale = i18n.language;
          const productNames = getFormattedProductList(results, locale);
          const sample = getFirstValidCoupon(results);
          const discount = formatDiscountMessage(sample!.discountType, sample!.discountValue);
          
          toast.success(t('pricing.coupon.validDynamic', { 
            products: productNames, 
            discount 
          }));
        }
      }

      if (hasAnyValid) {
        saveCouponData(code.toUpperCase(), results);
      }
    } catch (error: any) {
      if (showToast) toast.error(error.message || t('pricing.coupon.validationError'));
      setAppliedCoupons({});
    } finally {
      setIsValidating(false);
    }
  };

  const validateCoupon = () => {
    validateCouponCode(couponCode, true);
  };

  const removeCoupon = () => {
    setCouponCode("");
    setAppliedCoupons({});
    clearCouponData();
    toast.success(t('pricing.coupon.removed'));
  };

  const calculateFinalPrice = (planId: string, originalPrice: number) => {
    const coupon = appliedCoupons[planId];
    if (!coupon) return originalPrice;
    
    // Handle different discount types correctly
    if (coupon.discountType === 'full') {
      return 0;
    } else if (coupon.discountType === 'fixed') {
      // Fixed discount: subtract the fixed amount
      return Math.max(0, originalPrice - coupon.discountValue);
    } else {
      // Percentage discount
      return originalPrice * (1 - coupon.discountPercent / 100);
    }
  };

  const handleCheckout = async (planId: string, billingType: string) => {
    if (!hasCompletedScreening || screeningStatus !== 'approved') {
      toast.error(t('pricing.error.screeningRequired'), {
        description: t('pricing.error.screeningRequiredDesc'),
      });
      navigate('/become-professional');
      return;
    }

    if (!isAuthenticated) {
      toast.error(t('pricing.errors.loginRequired'));
      navigate("/auth/login");
      return;
    }

    // VISIBILITY PACKAGES: richiedono piano attivo, niente upgrade/downgrade
    if (isVisibilityPackage(planId)) {
      if (!hasActiveSubscription) {
        toast.error(t('pricing.error.subscriptionRequired'), {
          description: t('pricing.error.subscriptionRequiredDesc'),
        });
        return;
      }
      // Acquisto diretto - NO modale upgrade/downgrade
      await processCheckout(planId, billingType);
      return;
    }

    // SUBSCRIPTION PLANS: logica upgrade/downgrade
    const action = getPlanAction(planId);
    
    if (action === 'upgrade') {
      setPendingPlanChange({ planId, billingType });
      setShowUpgradeDialog(true);
      return;
    }
    
    if (action === 'downgrade') {
      setPendingPlanChange({ planId, billingType });
      setShowDowngradeDialog(true);
      return;
    }

    // Normal checkout for new subscriptions
    await processCheckout(planId, billingType);
  };

  const processCheckout = async (planId: string, billingType: string) => {
    setIsCheckingOut(true);
    
    // Get affiliate code from localStorage
    const affiliateCode = getStoredAffiliateCode();
    
    // Only send coupon if it's valid for this specific plan
    const couponForPlan = appliedCoupons[planId] ? couponCode.toUpperCase() : undefined;
    
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          planType: planId,
          billingType,
          couponCode: couponForPlan,
          affiliateCode: affiliateCode || undefined,
        },
      });

      if (error) throw error;

      if (data.success) {
        // 100% discount - subscription activated directly
        toast.success(data.message);
        await refreshSubscription();
        navigate("/dashboard");
      } else if (data.url) {
        // Redirect to Stripe checkout
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      toast.error(error.message || t('pricing.errors.checkoutError'));
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleUpgradeConfirm = async () => {
    if (!pendingPlanChange) return;
    
    setShowUpgradeDialog(false);
    setIsCheckingOut(true);

    try {
      const { data, error } = await supabase.functions.invoke("update-subscription", {
        body: { newPlanType: pendingPlanChange.planId },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message);
        // Refresh page to update subscription state
        window.location.reload();
      }
    } catch (error: any) {
      toast.error(error.message || t('pricing.errors.upgradeError'));
    } finally {
      setIsCheckingOut(false);
      setPendingPlanChange(null);
    }
  };

  const handleDowngradeConfirm = async () => {
    if (!pendingPlanChange) return;
    
    setShowDowngradeDialog(false);
    setIsCheckingOut(true);

    try {
      const { data, error } = await supabase.functions.invoke("update-subscription", {
        body: { newPlanType: pendingPlanChange.planId },
      });

      if (error) throw error;

      if (data.success) {
        toast.success(data.message);
        // Refresh page to update subscription state
        window.location.reload();
      }
    } catch (error: any) {
      toast.error(error.message || t('pricing.errors.downgradeError'));
    } finally {
      setIsCheckingOut(false);
      setPendingPlanChange(null);
    }
  };

  // Loading subscription state
  if (isAuthenticated && loadingSubscription) {
    return (
      <div className="min-h-screen flex flex-col bg-surface">
        <SEO title={t('pricing.seoTitle')} description={t('pricing.seoDescription')} />
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="font-body text-muted-foreground">{t('pricing.loading')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <SEO
        title={t('pricing.seoTitle')}
        description={t('pricing.seoDescription')}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <SEOBreadcrumb items={[{ label: t('pricing.breadcrumb') }]} />
            <h1 className="font-heading font-bold text-4xl md:text-6xl text-dark mb-4">
              {t('pricing.heroTitle')}
            </h1>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('pricing.heroSubtitle')}
            </p>
          </div>

          {/* Coupon Form */}
          <BrutalCard className="max-w-xl mx-auto mb-12">
            <div className="flex flex-col sm:flex-row gap-4">
              <BrutalInput
                placeholder={t('pricing.coupon.placeholder')}
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="flex-1"
                disabled={hasAnyCouponApplied}
              />
              {!hasAnyCouponApplied ? (
                <BrutalButton
                  onClick={validateCoupon}
                  disabled={isValidating}
                  className="whitespace-nowrap"
                >
                  {isValidating ? t('pricing.coupon.validating') : t('pricing.coupon.apply')}
                </BrutalButton>
              ) : (
                <BrutalButton
                  onClick={removeCoupon}
                  variant="outline"
                  className="whitespace-nowrap flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  {t('pricing.coupon.remove')}
                </BrutalButton>
              )}
            </div>
            {hasAnyCouponApplied && (() => {
              const sample = getSampleValidCoupon();
              if (!sample) return null;
              const discount = formatDiscountMessage(sample.discountType, sample.discountValue);
              const locale = i18n.language || 'it';
              const formattedProducts = getFormattedProductList(appliedCoupons, locale);
              return (
                <div className="mt-4 p-4 bg-primary/10 border-[3px] border-primary">
                  <p className="font-heading font-bold text-dark">
                    {t('pricing.coupon.couponConfirmation', { discount })}
                  </p>
                  <p className="font-body text-sm text-muted-foreground mt-1">
                    {t('pricing.coupon.appliesToFormatted', { products: formattedProducts })}
                  </p>
                  {sample.couponDescription && (
                    <p className="font-body text-sm text-muted-foreground mt-1">
                      {sample.couponDescription}
                    </p>
                  )}
                </div>
              );
            })()}
          </BrutalCard>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {visibleRecurringPlans.map((plan) => {
              const finalPrice = calculateFinalPrice(plan.id, plan.price);
              const hasDiscount = finalPrice < plan.price && appliedCoupons[plan.id];
              const action = getPlanAction(plan.id);
              const isCurrent = action === 'current';
              
              let buttonText = t('pricing.plans.selectPlan');
              let buttonVariant: "primary" | "outline" | "secondary" = plan.popular ? "primary" : "outline";
              let buttonDisabled = isCheckingOut || loadingSubscription;
              
              if (!isAuthenticated || !hasCompletedScreening || screeningStatus !== 'approved') {
                buttonText = t('pricing.plans.becomeVerified');
                buttonVariant = "primary";
              } else if (action === 'current') {
                buttonText = t('pricing.plans.currentPlan');
                buttonVariant = "secondary";
                buttonDisabled = true;
              } else if (action === 'upgrade') {
                buttonText = t('pricing.plans.upgrade');
                buttonVariant = "primary";
              } else if (action === 'downgrade') {
                buttonText = t('pricing.plans.downgrade');
                buttonVariant = "outline";
              }

              return (
                <BrutalCard
                  key={plan.id}
                  className={`relative ${
                    plan.popular ? "border-primary border-[4px]" : ""
                  } ${isCurrent ? "border-success border-[4px] bg-success/5" : ""}`}
                >
                  {plan.popular && !isCurrent && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary px-4 py-1 border-[3px] border-dark">
                      <span className="font-heading font-bold text-xs uppercase">
                        {t('pricing.plans.popular')}
                      </span>
                    </div>
                  )}
                  
                  {isCurrent && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-success px-4 py-1 border-[3px] border-dark">
                      <span className="font-heading font-bold text-xs uppercase text-white">
                        {t('pricing.plans.yourPlan')}
                      </span>
                    </div>
                  )}

                  <h3 className="font-heading font-bold text-2xl text-dark mb-2">
                    {t(plan.nameKey)}
                  </h3>

                  <div className="mb-6">
                    {hasDiscount && (
                      <p className="font-heading text-lg text-muted-foreground line-through">
                        €{plan.price.toFixed(2)}
                      </p>
                    )}
                    <p className="font-heading font-bold text-4xl text-dark">
                      €{finalPrice.toFixed(2)}
                      <span className="text-base font-normal text-muted-foreground">
                        {t('pricing.plans.perYear')}
                      </span>
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {(t(plan.featuresKey, { returnObjects: true }) as string[]).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 font-body">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {action === 'downgrade' ? (
                    <button
                      onClick={() => handleCheckout(plan.id, plan.duration)}
                      disabled={buttonDisabled}
                      className="w-full text-center text-muted-foreground hover:text-foreground underline font-body text-sm py-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {buttonText}
                    </button>
                  ) : (
                    <BrutalButton
                      className="w-full"
                      variant={buttonVariant}
                      onClick={() => {
                        if (!isAuthenticated) {
                          navigate('/auth/register?returnTo=/become-professional');
                        } else if (!hasCompletedScreening || screeningStatus !== 'approved') {
                          navigate('/become-professional');
                        } else {
                          handleCheckout(plan.id, plan.duration);
                        }
                      }}
                      disabled={buttonDisabled}
                    >
                      {buttonText}
                    </BrutalButton>
                  )}
                </BrutalCard>
              );
            })}
          </div>

          {/* See All Features Link for Subscription Plans */}
          <div className="text-center mt-2 mb-8">
            <button
              onClick={() => navigate("/subscription-plans")}
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-dark underline underline-offset-4 transition-colors"
            >
              {t("subscriptionPlans.seeAllFeatures")}
            </button>
          </div>

          {/* Visibility Packages */}
          {visibleVisibilityPackages.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center justify-center gap-2 mb-4">
                <h2 className="font-heading font-bold text-3xl text-dark text-center">
                  {t('pricing.visibility.title')}
                </h2>
                <button
                  onClick={() => setShowVisibilityInfo(!showVisibilityInfo)}
                  className="p-1 hover:bg-muted rounded-full transition-colors"
                  aria-label="Info"
                >
                  <Info className="w-5 h-5 text-muted-foreground hover:text-dark" />
                </button>
              </div>
              
              {showVisibilityInfo && (
                <div className="max-w-3xl mx-auto mb-8">
                  <BrutalCard className="bg-gradient-to-br from-yellow-50 to-orange-50">
                    <div className="flex items-start gap-4">
                      <div className="text-4xl">⭐</div>
                      <div className="flex-1">
                        <h3 className="font-heading font-bold text-lg mb-2">
                          {t('pricing.visibility.howItWorks.title')}
                        </h3>
                        <ul className="space-y-2 text-sm font-body">
                          <li className="flex items-start gap-2">
                            <span className="text-primary font-bold">1.</span>
                            <span>{t('pricing.visibility.howItWorks.step1')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary font-bold">2.</span>
                            <span>{t('pricing.visibility.howItWorks.step2')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary font-bold">3.</span>
                            <span>{t('pricing.visibility.howItWorks.step3')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary font-bold">4.</span>
                            <span>{t('pricing.visibility.howItWorks.step4')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </BrutalCard>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {visibleVisibilityPackages.map((plan) => {
                const finalPrice = calculateFinalPrice(plan.id, plan.price);
                const hasDiscount = finalPrice < plan.price && appliedCoupons[plan.id];
                const isActive = (plan.id === "spotlight" && hasActiveSpotlight) 
                              || (plan.id === "social_boost" && hasActiveSocialBoost);
                const endDate = plan.id === "spotlight" ? spotlightEndDate : socialBoostEndDate;
                const daysRemaining = endDate 
                  ? differenceInDays(endDate, new Date())
                  : 0;

                return (
                  <BrutalCard 
                    key={plan.id}
                    className={`relative ${isActive ? "border-primary border-[4px] bg-primary/5" : ""}`}
                  >
                    {isActive && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary px-4 py-1 border-[3px] border-dark">
                        <span className="font-heading font-bold text-xs uppercase text-primary-foreground">
                          {t("dashboard.upsell.active")}
                        </span>
                      </div>
                    )}
                    
                    <h3 className="font-heading font-bold text-2xl text-dark mb-2">
                      {t(plan.nameKey)}
                    </h3>

                    <div className="mb-6">
                      {isActive ? (
                        <p className="font-heading font-bold text-2xl text-primary">
                          {t("dashboard.upsell.activeFor", { days: daysRemaining })}
                        </p>
                      ) : (
                        <>
                          {hasDiscount && (
                            <p className="font-heading text-lg text-muted-foreground line-through">
                              €{plan.price.toFixed(2)}
                            </p>
                          )}
                          <p className="font-heading font-bold text-4xl text-dark">
                            €{finalPrice.toFixed(2)}
                            <span className="text-base font-normal text-muted-foreground">
                              {" "}
                              {t('pricing.plans.oneTime')}
                            </span>
                          </p>
                        </>
                      )}
                    </div>

                    <TooltipProvider>
                      <ul className="space-y-3 mb-8">
                        {plan.id === 'social_boost' && (
                          <li className="flex items-start gap-2 font-body">
                            <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{t('pricing.plans.socialBoost.includesSpotlight')}</span>
                          </li>
                        )}
                        {(t(plan.featuresKey, { returnObjects: true }) as Array<{key: string, label: string}>).map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 font-body">
                            <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature.label}</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-4 h-4 text-muted-foreground hover:text-dark cursor-help flex-shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>{t(`visibilityPackages.table.features.${feature.key}.description`)}</p>
                              </TooltipContent>
                            </Tooltip>
                          </li>
                        ))}
                      </ul>
                    </TooltipProvider>

                    {(() => {
                      // Determine mutual exclusion for visibility packages
                      const otherPackageActive = plan.id === 'spotlight' ? hasActiveSocialBoost : hasActiveSpotlight;
                      const otherPackageName = plan.id === 'spotlight' 
                        ? t("dashboard.upsell.packages.social_boost.name")
                        : t("dashboard.upsell.packages.spotlight.name");
                      const otherPackageEndDate = plan.id === 'spotlight' ? socialBoostEndDate : spotlightEndDate;
                      const otherDaysRemaining = otherPackageEndDate 
                        ? differenceInDays(otherPackageEndDate, new Date())
                        : 0;

                      if (isActive) {
                        return (
                          <BrutalButton className="w-full" variant="secondary" disabled>
                            {t('dashboard.upsell.alreadyActive')}
                          </BrutalButton>
                        );
                      }
                      
                      if (otherPackageActive) {
                        return (
                          <BrutalButton className="w-full" variant="secondary" disabled>
                            {t("dashboard.upsell.otherPackageActiveFor", { 
                              package: otherPackageName, 
                              days: otherDaysRemaining 
                            })}
                          </BrutalButton>
                        );
                      }
                      
                      if (!hasActiveSubscription) {
                        return (
                          <div className="text-center">
                            <BrutalButton className="w-full" variant="secondary" disabled>
                              {t('pricing.plans.requiresSubscription')}
                            </BrutalButton>
                            <p className="text-xs text-muted-foreground mt-2">
                              {t('pricing.plans.requiresSubscriptionDesc')}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <BrutalButton
                          className="w-full"
                          onClick={() => handleCheckout(plan.id, plan.duration)}
                          disabled={isCheckingOut}
                        >
                          {isCheckingOut ? t('pricing.plans.loading') : t('pricing.plans.buyNow')}
                        </BrutalButton>
                      );
                    })()}
            </BrutalCard>
          );
        })}
      </div>

              {/* See All Features Link */}
              <div className="text-center mt-6">
                <button
                  onClick={() => navigate("/visibility-packages")}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-dark underline underline-offset-4 transition-colors"
                >
                  {t("pricing.visibility.seeAllFeatures")}
                </button>
              </div>
    </div>
  )}
        </div>
      </main>

      <Footer />

      {/* Upgrade Confirmation Dialog */}
      <AlertDialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pricing.upgrade.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pricing.upgrade.confirmDesc', {
                plan: pendingPlanChange?.planId ? planHierarchy[pendingPlanChange.planId as keyof typeof planHierarchy]?.name : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingPlanChange(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleUpgradeConfirm} disabled={isCheckingOut}>
              {isCheckingOut ? t('pricing.plans.loading') : t('pricing.upgrade.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Downgrade Confirmation Dialog */}
      <DowngradeModal
        open={showDowngradeDialog}
        onOpenChange={setShowDowngradeDialog}
        currentPlan={subscriptionType || ''}
        targetPlan={pendingPlanChange?.planId || ''}
        renewalDate={subscriptionEndDate}
        isProcessing={isCheckingOut}
        onConfirm={handleDowngradeConfirm}
        onCancel={() => {
          setShowDowngradeDialog(false);
          setPendingPlanChange(null);
        }}
      />
    </div>
  );
};

export default Pricing;
